import {FC, useCallback, useContext} from 'react';
import {Link} from 'react-router-dom';
import {Table} from 'semantic-ui-react';
import {isEmpty, map, find} from 'lodash';
import {observer} from 'mobx-react';

import {getPossibleStageValues, getProcessorByStageName, getOutputNameByStageName} from '../stageUtils';
import generateProbeURI from '../generateProbeURI';
import IBAContext from '../IBAContext';
import {Probe, ProbeProcessor, ProcessorDefinition} from '../types';

type ProcessorInputsProps = {
  probe: Probe;
  processor: ProbeProcessor;
  highlightStage: (stageName: string, highlight?: boolean) => void;
  processorDefinitionsByName: Record<string, ProcessorDefinition>;
}

const ProcessorInputs: FC<ProcessorInputsProps> = ({probe, processor, highlightStage, processorDefinitionsByName}) => {
  const {blueprintId} = useContext(IBAContext);

  const formatColumnName = useCallback((stageName, columnName) => {
    if (columnName === '*') {
      return (
        <b>{'All Columns'}</b>
      );
    }
    const sourceProcessor = getProcessorByStageName({probe, stageName});
    if (sourceProcessor) {
      const sourceProcessorDefinition = processorDefinitionsByName[sourceProcessor.name];
      const sourceOutputName = getOutputNameByStageName({processor: sourceProcessor, stageName});
      if (sourceProcessorDefinition && sourceOutputName) {
        const inputValues = getPossibleStageValues(sourceProcessorDefinition.outputs[sourceOutputName]);
        const columnSchema = find(inputValues, {name: columnName});
        return columnSchema?.title ?? columnName;
      }
    }
    return columnName;
  }, [probe, processorDefinitionsByName]);

  if (isEmpty(processor.inputs)) return null;

  return (
    <Table celled size='small'>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell colSpan='3'>{'Inputs'}</Table.HeaderCell>
        </Table.Row>
        <Table.Row>
          <Table.HeaderCell collapsing>{'Input Name'}</Table.HeaderCell>
          <Table.HeaderCell>{'Stage Name'}</Table.HeaderCell>
          <Table.HeaderCell>{'Column Name'}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {map(processor.inputs, ({stage: stageName, column: columnName}, inputName) =>
          <Table.Row key={inputName}>
            <Table.Cell>{inputName}</Table.Cell>
            <Table.Cell>
              <Link
                to={generateProbeURI({blueprintId, probeId: probe.id, stageName})}
                onMouseEnter={stageName ? () => highlightStage(stageName, true) : undefined}
                onMouseLeave={stageName ? () => highlightStage(stageName, false) : undefined}
                onClick={stageName ? () => highlightStage(stageName, false) : undefined}
              >
                {stageName}
              </Link>
            </Table.Cell>
            <Table.Cell>
              {formatColumnName(stageName, columnName)}
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
};

export default observer(ProcessorInputs);
